import { Transaction } from '@mysten/sui/transactions';
import { CPC_FRAMEWORK, DGC_TYPE } from './constants.js';
import { mistToDgc } from './amount.js';
import { quoteDataMistForDgcBid } from './clobMarket.js';
/** 1 DGC lot = 1 DGC (9 decimals). Also 1 DATA lot size for quote mist display. */
export const MIST_PER_DATA_LOT = 1000000000n;
export const MIST_PER_RWAX_LOT = 1000000000n;
export function resolveLotSizeMist(lotSizeMist) {
    if (lotSizeMist == null || lotSizeMist === '')
        return MIST_PER_RWAX_LOT;
    const n = BigInt(lotSizeMist);
    if (n <= 0n)
        throw new Error('lotSizeMist must be > 0');
    return n;
}
/**
 * CLOB gas 预算上限（写入 PTB，非实扣）。
 * 与质押 budget 同量级（0.01 DGC）；普通挂单足够。
 */
export const CPC_CLOB_GAS_BUDGET_MIST = 10000000n;
/**
 * 卖单从 AB 扣 DGC 时预留的 gas 余量。
 * 必须 ≥ {@link CPC_CLOB_GAS_BUDGET_MIST}，与预算对齐。
 */
export const CPC_CLOB_AB_GAS_RESERVE_MIST = CPC_CLOB_GAS_BUDGET_MIST;
/** 默认 pair fee（与链上 register 一致，本地 dev 30 bps）。 */
export const DATA_CLOB_DEFAULT_FEE_BPS = 30n;
function applyClobAddressBalanceGas(tx) {
    tx.setGasPayment([]);
    // 始终写入，避免 dApp/钱包 prepare 用质押默认 budget 覆盖
    tx.setGasBudget(CPC_CLOB_GAS_BUDGET_MIST);
}
/**
 * CLOB 买单（P-UX）：从地址余额划出 quote（DATA），找零回 AB。
 * 对应 Move `order_book::place_bid_from_address_balance<Base, Quote>`。
 *
 * 注意：链上预检在执行前校验 FundsWithdrawal，同笔 PTB 内 send_funds 不能抬高可用 AB。
 * DATA 仅在零散 Coin 时请用 {@link buildPlaceBidForOwner}（改走 Coin + `place_bid`）。
 */
export function buildPlaceBidFromAddressBalance({ packageId, orderBookId, registryId, baseType, quoteType, quoteAmountMist, priceTicks, qtyLots, owner: _owner, looseQuoteCoinObjectIds = [], }) {
    if (looseQuoteCoinObjectIds.length > 0) {
        // 兼容旧调用：忽略并提示应用 ForOwner（其内部会走 Coin 路径）
        console.warn('[cpc-tx] buildPlaceBidFromAddressBalance: looseQuoteCoinObjectIds 已忽略（同笔合并无法通过预检）。请用 buildPlaceBidForOwner。');
    }
    const tx = new Transaction();
    const quoteMist = BigInt(quoteAmountMist);
    const withdrawal = tx.withdrawal({ amount: quoteMist, type: quoteType });
    tx.moveCall({
        target: `${packageId}::order_book::place_bid_from_address_balance`,
        typeArguments: [baseType, quoteType],
        arguments: [
            tx.object(orderBookId),
            tx.object(registryId),
            withdrawal,
            tx.pure.u64(priceTicks),
            tx.pure.u64(qtyLots),
        ],
    });
    applyClobAddressBalanceGas(tx);
    return tx;
}
/**
 * 买单：零散 `Coin<DATA>` split 精确 quote 后 `place_bid`（避免 AB=0 时 withdrawal 预检失败）。
 * `place_bid` 会吞掉整枚 Coin，必须 split 出精确金额。
 */
export function buildPlaceBidFromLooseCoins(params, coins) {
    const quoteMist = BigInt(params.quoteAmountMist);
    const priceTicks = BigInt(params.priceTicks);
    const qtyLots = BigInt(params.qtyLots);
    if (coins.length === 0) {
        throw new Error('无可用 DATA Coin');
    }
    const tx = new Transaction();
    let source = tx.object(coins[0].coinObjectId);
    const single = coins.find((c) => BigInt(c.balance) >= quoteMist);
    if (single) {
        source = tx.object(single.coinObjectId);
    }
    else if (coins.length > 1) {
        for (let i = 1; i < coins.length; i++) {
            tx.moveCall({
                target: `${CPC_FRAMEWORK}::coin::join`,
                typeArguments: [params.quoteType],
                arguments: [source, tx.object(coins[i].coinObjectId)],
            });
        }
    }
    const [bidCoin] = tx.splitCoins(source, [tx.pure.u64(quoteMist)]);
    tx.moveCall({
        target: `${params.packageId}::order_book::place_bid`,
        typeArguments: [params.baseType, params.quoteType],
        arguments: [
            tx.object(params.orderBookId),
            tx.object(params.registryId),
            bidCoin,
            tx.pure.u64(priceTicks),
            tx.pure.u64(qtyLots),
        ],
    });
    applyClobAddressBalanceGas(tx);
    return tx;
}
/**
 * 买单：零散 `Coin<DATA>` 够则优先 `place_bid`（split 精确金额）；
 * 否则才走地址余额 withdrawal。
 *
 * 不可「AB 字段看起来够」却实际为 0，也不可同笔 send_funds+withdrawal
 *（预检时 AB 仍为 0 → `0 < quoteMist`）。
 */
export async function buildPlaceBidForOwner(client, owner, params) {
    const quoteMist = BigInt(params.quoteAmountMist);
    const bal = await client.getBalance({ owner, coinType: params.quoteType });
    const totalMist = BigInt(bal.totalBalance ?? 0);
    // 切勿用 totalBalance 冒充 AB；缺字段时按 0
    const addressMist = BigInt(bal.fundsInAddressBalance ?? 0);
    if (totalMist < quoteMist) {
        const quoteSym = params.quoteType.includes('::data::DATA')
            ? 'DATA'
            : params.quoteType.split('::').pop() || 'quote';
        throw new Error(`${quoteSym} 不足：需约 ${mistToDgc(quoteMist)} ${quoteSym}，持有约 ${mistToDgc(totalMist)} ${quoteSym}` +
            `（买入 base 扣的是报价币，不是 base 本身）`);
    }
    const coins = await fetchDataCoins(client, owner, params.quoteType);
    const coinTotal = coins.reduce((s, c) => s + BigInt(c.balance), 0n);
    // 优先 Coin：覆盖「UI 显示有 DATA、但全在零散 Coin、AB=0」的常见发行/领水场景
    if (coinTotal >= quoteMist) {
        return buildPlaceBidFromLooseCoins({ ...params, quoteAmountMist: quoteMist }, coins);
    }
    if (addressMist >= quoteMist) {
        return buildPlaceBidFromAddressBalance({
            ...params,
            quoteAmountMist: quoteMist,
        });
    }
    const quoteSym = params.quoteType.includes('::data::DATA')
        ? 'DATA'
        : params.quoteType.split('::').pop() || 'quote';
    throw new Error(`${quoteSym} 不足以下单：需 ${mistToDgc(quoteMist)}，地址余额 ${mistToDgc(addressMist)}，` +
        `零散 Coin ${mistToDgc(coinTotal)}（买入 base 花费的是报价资产 ${quoteSym}）`);
}
/** @deprecated use `dgcBaseMistForLots` */
export function dataBaseMistForLots(qtyLots, lotSizeMist = MIST_PER_RWAX_LOT) {
    return qtyLots * lotSizeMist;
}
export function dgcBaseMistForLots(qtyLots, lotSizeMist = MIST_PER_RWAX_LOT) {
    return qtyLots * lotSizeMist;
}
/**
 * CLOB 卖单（P-UX）：从地址余额划出 base（DGC）→ Coin → `place_ask`。
 */
export function buildPlaceAskFromAddressBalance({ packageId, orderBookId, registryId, baseType, quoteType, priceTicks, qtyLots, lotSizeMist, }) {
    const tx = new Transaction();
    const baseMist = dgcBaseMistForLots(BigInt(qtyLots), resolveLotSizeMist(lotSizeMist));
    const withdrawal = tx.withdrawal({ amount: baseMist, type: baseType });
    const balance = tx.moveCall({
        target: `${CPC_FRAMEWORK}::balance::redeem_funds`,
        typeArguments: [baseType],
        arguments: [withdrawal],
    });
    const coin = tx.moveCall({
        target: `${CPC_FRAMEWORK}::coin::from_balance`,
        typeArguments: [baseType],
        arguments: [balance],
    });
    tx.moveCall({
        target: `${packageId}::order_book::place_ask`,
        typeArguments: [baseType, quoteType],
        arguments: [
            tx.object(orderBookId),
            tx.object(registryId),
            coin,
            tx.pure.u64(priceTicks),
            tx.pure.u64(qtyLots),
        ],
    });
    applyClobAddressBalanceGas(tx);
    return tx;
}
/** 卖单成交后卖方收到的 DATA（扣 fee 后净值估算）。 */
export function estimateQuoteProceedsForDgcAsk(priceTicks, qtyLots, feeBps = DATA_CLOB_DEFAULT_FEE_BPS, lotSizeMist = MIST_PER_RWAX_LOT) {
    const gross = quoteDataMistForDgcBid(priceTicks, qtyLots, lotSizeMist);
    return gross - (gross * feeBps) / 10000n;
}
/** @deprecated use `estimateQuoteProceedsForDgcAsk` */
export function estimateCpcProceedsForDataAsk(priceTicks, qtyLots, feeBps = DATA_CLOB_DEFAULT_FEE_BPS, lotSizeMist = MIST_PER_RWAX_LOT) {
    return estimateQuoteProceedsForDgcAsk(priceTicks, qtyLots, feeBps, lotSizeMist);
}
export async function fetchDataCoins(client, owner, coinType) {
    const coins = [];
    let cursor = undefined;
    for (;;) {
        const resp = await client.getCoins({ owner, coinType, cursor });
        for (const c of resp.data ?? []) {
            if (BigInt(c.balance) > 0n) {
                coins.push({ coinObjectId: c.coinObjectId, balance: c.balance });
            }
        }
        if (!resp.hasNextPage || !resp.nextCursor)
            break;
        cursor = resp.nextCursor;
    }
    return coins;
}
function buildPlaceAskFromLooseCoins(params, coins, baseMist) {
    const tx = new Transaction();
    let source = tx.object(coins[0].coinObjectId);
    const single = coins.find((c) => BigInt(c.balance) >= baseMist);
    if (single) {
        source = tx.object(single.coinObjectId);
    }
    else if (coins.length > 1) {
        for (let i = 1; i < coins.length; i++) {
            tx.moveCall({
                target: `${CPC_FRAMEWORK}::coin::join`,
                typeArguments: [params.baseType],
                arguments: [source, tx.object(coins[i].coinObjectId)],
            });
        }
    }
    const [askCoin] = tx.splitCoins(source, [tx.pure.u64(baseMist)]);
    tx.moveCall({
        target: `${params.packageId}::order_book::place_ask`,
        typeArguments: [params.baseType, params.quoteType],
        arguments: [
            tx.object(params.orderBookId),
            tx.object(params.registryId),
            askCoin,
            tx.pure.u64(params.priceTicks),
            tx.pure.u64(params.qtyLots),
        ],
    });
    applyClobAddressBalanceGas(tx);
    return tx;
}
/**
 * 卖单：AB 够「卖量 + gas 余量」才走地址余额；否则优先零散 `Coin<RWAX>` split。
 * 禁止「AB 刚好等于卖量却仍走 AB」——会与 AB gas 抢同一池资金。
 */
export async function buildPlaceAskForOwner(client, owner, params) {
    const qtyLots = BigInt(params.qtyLots);
    const priceTicks = BigInt(params.priceTicks);
    const lotSizeMist = resolveLotSizeMist(params.lotSizeMist);
    const baseMist = dgcBaseMistForLots(qtyLots, lotSizeMist);
    const bal = await client.getBalance({ owner, coinType: params.baseType });
    const totalMist = BigInt(bal.totalBalance ?? 0);
    const addressMist = BigInt(bal.fundsInAddressBalance ?? 0);
    if (totalMist < baseMist) {
        throw new Error(`余额不足：需 ${mistToDgc(baseMist)}，持有约 ${mistToDgc(totalMist)}`);
    }
    const coins = await fetchDataCoins(client, owner, params.baseType);
    const coinTotal = coins.reduce((s, c) => s + BigInt(c.balance), 0n);
    const abComfortable = addressMist >= baseMist + CPC_CLOB_AB_GAS_RESERVE_MIST;
    if (abComfortable) {
        return buildPlaceAskFromAddressBalance({ ...params, priceTicks, qtyLots });
    }
    // AB 不够 gas 余量时优先用零散 Coin 出货，gas 仍从 AB 付（不占用卖单额度）
    if (coinTotal >= baseMist) {
        if (addressMist <= 0n) {
            throw new Error(`DGC 卖单需少量地址余额付 Gas（约 ${mistToDgc(CPC_CLOB_AB_GAS_RESERVE_MIST)} DGC）。` +
                `当前 DGC 均在零散 Coin 中，请先合并一小部分到地址余额后再卖。`);
        }
        return buildPlaceAskFromLooseCoins({ ...params, priceTicks, qtyLots }, coins, baseMist);
    }
    if (addressMist >= baseMist) {
        throw new Error(`地址余额不足 Gas 余量：卖 ${mistToDgc(baseMist)} 后需另留约 ${mistToDgc(CPC_CLOB_AB_GAS_RESERVE_MIST)}。` +
            `当前地址余额 ${mistToDgc(addressMist)}；请减少卖量，或先保留/合并出零散 Coin 后再卖。`);
    }
    throw new Error(`余额不足：需 ${mistToDgc(baseMist)}，地址余额 ${mistToDgc(addressMist)}，零散 Coin ${mistToDgc(coinTotal)}`);
}
/** 撤单：对应 Move `order_book::cancel_order`. */
export function buildCancelOrder({ packageId, orderBookId, registryId, baseType, quoteType, orderId, }) {
    const tx = new Transaction();
    tx.moveCall({
        target: `${packageId}::order_book::cancel_order`,
        typeArguments: [baseType, quoteType],
        arguments: [
            tx.object(orderBookId),
            tx.object(registryId),
            tx.pure.u64(orderId),
        ],
    });
    applyClobAddressBalanceGas(tx);
    return tx;
}
/** Registry admin 撤任意一笔。对应 `order_book::admin_cancel_order`. */
export function buildAdminCancelOrder({ packageId, orderBookId, registryId, baseType, quoteType, orderId, }) {
    const tx = new Transaction();
    tx.moveCall({
        target: `${packageId}::order_book::admin_cancel_order`,
        typeArguments: [baseType, quoteType],
        arguments: [
            tx.object(orderBookId),
            tx.object(registryId),
            tx.pure.u64(orderId),
        ],
    });
    applyClobAddressBalanceGas(tx);
    return tx;
}
/** Registry admin 按 id 区间撤单。对应 `order_book::admin_cancel_range`. */
export function buildAdminCancelRange({ packageId, orderBookId, registryId, baseType, quoteType, fromId, toId, }) {
    const tx = new Transaction();
    tx.moveCall({
        target: `${packageId}::order_book::admin_cancel_range`,
        typeArguments: [baseType, quoteType],
        arguments: [
            tx.object(orderBookId),
            tx.object(registryId),
            tx.pure.u64(fromId),
            tx.pure.u64(toId),
        ],
    });
    applyClobAddressBalanceGas(tx);
    return tx;
}
/** Registry admin 清整本簿。对应 `order_book::admin_cancel_all`. */
export function buildAdminCancelAll({ packageId, orderBookId, registryId, baseType, quoteType, }) {
    const tx = new Transaction();
    tx.moveCall({
        target: `${packageId}::order_book::admin_cancel_all`,
        typeArguments: [baseType, quoteType],
        arguments: [tx.object(orderBookId), tx.object(registryId)],
    });
    applyClobAddressBalanceGas(tx);
    return tx;
}
/** Registry admin 暂停 / 恢复交易对。对应 `registry::set_pair_paused`. */
export function buildSetPairPaused({ packageId, registryId, pairId, paused, }) {
    const tx = new Transaction();
    tx.moveCall({
        target: `${packageId}::registry::set_pair_paused`,
        arguments: [tx.object(registryId), tx.pure.u64(pairId), tx.pure.bool(paused)],
    });
    applyClobAddressBalanceGas(tx);
    return tx;
}
/** Registry admin 改一手。对应 `registry::set_lot_size`. 须先撤光该对挂单。 */
export function buildSetLotSize({ packageId, registryId, pairId, lotSize, }) {
    const tx = new Transaction();
    tx.moveCall({
        target: `${packageId}::registry::set_lot_size`,
        arguments: [tx.object(registryId), tx.pure.u64(pairId), tx.pure.u64(lotSize)],
    });
    applyClobAddressBalanceGas(tx);
    return tx;
}
/** Convenience: DGC/DATA type args. */
export function dgcDataTypeArgs(dataTypeTag) {
    return [DGC_TYPE, dataTypeTag];
}
